/**
 * Asking the person's own agent to do something, from a screen.
 *
 * Two of these exist so far: "how much of this project is done?" and "sync this project's
 * documentation into Vephus". Both are the same shape — Vephus cannot do it, the agent with the
 * repository open can — so both go through here.
 *
 * Three rules, and they are why this is a panel rather than a button:
 *
 * 1. **The command is shown in full before it is sent.** A command written by a screen and
 *    delivered to somebody's agent should never be a surprise.
 * 2. **What it needs is said plainly**, including the parts DevPulse cannot check — that the agent
 *    has the repository open, and that what it will read is up to date.
 * 3. **It only ever goes to the person's own agents**, through remote control, which is off until
 *    they turn it on.
 */

import { useState, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Bot, ShieldAlert } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'

export function AskAgent({
  label,
  icon,
  what,
  needs,
  prompt,
  sent,
  failed,
  after,
}: {
  label: string
  icon: ReactNode
  /** Why this is asked of their AI rather than answered by DevPulse. */
  what: string
  /** What it needs beyond remote control — the parts DevPulse cannot check. */
  needs: string[]
  prompt: string
  /** What happens next, and where the answer appears. */
  sent: string
  failed: string
  /** Anything else to do when the command is accepted. A refusal here is ignored. */
  after?: () => Promise<unknown>
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)

  const { data: remote } = useQuery({ queryKey: ['remote'], queryFn: api.remote.overview, enabled: open, retry: false })

  const ask = useMutation({
    mutationFn: async () => {
      await api.remote.send(prompt, null)
      await after?.().catch(() => undefined)
    },
  })

  const listening = (remote?.agents ?? []).some((agent) => agent.waiting || agent.working)
  const ready = Boolean(remote?.allowed && remote?.enabled)

  return (
    <>
      <button type="button" className="btn btn--sm" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        {icon} {label}
      </button>

      {open && (
        <div className="stack stack--tight estimate-ask">
          <p className="small secondary">{what}</p>

          <ul className="estimate-ask__needs small">
            <li className={ready ? 'estimate-ask__ok' : 'estimate-ask__missing'}>
              {t('estimate.needRemote')}
              {!ready && <> — <Link to="/agents/remote">{t('nav.remote')}</Link></>}
            </li>
            <li className={listening ? 'estimate-ask__ok' : 'estimate-ask__missing'}>
              {listening ? t('estimate.agentListening') : t('estimate.needAgent')}
            </li>
            {needs.map((need) => <li key={need}>{need}</li>)}
          </ul>

          <details className="estimate-ask__prompt">
            <summary className="tiny muted">{t('estimate.seePrompt')}</summary>
            <pre className="code">{prompt}</pre>
          </details>

          {ask.isSuccess ? (
            <div className="row row--tight small" style={{ color: 'var(--ok-text)' }}>
              <Bot size={14} aria-hidden /> {sent}
            </div>
          ) : (
            <div className="row row--tight">
              <button type="button" className="btn btn--sm btn--primary" onClick={() => ask.mutate()} disabled={!ready || ask.isPending}>
                {ask.isPending ? t('common.saving') : t('estimate.send')}
              </button>
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => setOpen(false)}>{t('common.cancel')}</button>
            </div>
          )}

          {ask.isError && (
            <div className="connect__warning small" role="alert">
              <ShieldAlert size={14} aria-hidden /> {failed}
            </div>
          )}
        </div>
      )}
    </>
  )
}
